import { useEffect, useState } from 'react'
import { Dialog } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import ServicoEditor from '@/components/contrato/ServicoEditor'

const tipos = [
  { value: 'servico', label: 'Serviço' },
  { value: 'valor', label: 'Valor' },
  { value: 'prazo', label: 'Prazo' },
  { value: 'valor-prazo', label: 'Valor e prazo' },
]

/**
 * Modal para adicionar ou editar um aditivo.
 * - `aditivo` nulo: novo aditivo; tipo servico/valor carrega os serviços do
 *   aditivo anterior (baseServicos) como base, editáveis.
 * - `aditivo` preenchido: edição; os campos e serviços vêm do próprio aditivo.
 * - tipo prazo: apenas datas, assinado e observação.
 */
export default function AditivoModal({ open, onClose, onSubmit, servicos, baseServicos = [], aditivo = null }) {
  const isEdit = !!aditivo
  const [tipo, setTipo] = useState('servico')
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [assinado, setAssinado] = useState(false)
  const [observacao, setObservacao] = useState('')
  const [servicoRows, setServicoRows] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setTipo(aditivo?.tipo || 'servico')
      setDataInicio((aditivo?.dataInicio || '').slice(0, 10))
      setDataFim((aditivo?.dataFim || '').slice(0, 10))
      setAssinado(!!aditivo?.assinado)
      setObservacao(aditivo?.observacao || '')
      setError('')
      setServicoRows(seedFromBase(aditivo ? aditivo.servicos : baseServicos))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, aditivo])

  function onTipoChange(t) {
    setTipo(t)
    if (t === 'prazo') setServicoRows([])
    else if (servicoRows.length === 0) setServicoRows(seedFromBase(aditivo ? aditivo.servicos : baseServicos))
  }

  async function submit() {
    setError('')
    if (!dataInicio || !dataFim) {
      setError('Informe as datas de início e fim.')
      return
    }
    setSaving(true)
    try {
      const payload = { tipo, dataInicio, dataFim, assinado: assinado ? 1 : 0, observacao }
      if (tipo !== 'prazo') {
        payload.servicos = servicoRows.filter((r) => r.servicoID)
      }
      await onSubmit(payload)
    } catch (err) {
      setError(err?.response?.data?.error || 'Erro ao salvar aditivo.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEdit ? 'Editar aditivo' : 'Novo aditivo'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? 'Salvando...' : (isEdit ? 'Salvar aditivo' : 'Adicionar aditivo')}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Tipo</Label>
          <Select value={tipo} onChange={(e) => onTipoChange(e.target.value)}>
            {tipos.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </Select>
        </div>
        <div className="flex items-end gap-2 pb-2">
          <input id="ass-adit" type="checkbox" checked={assinado} onChange={(e) => setAssinado(e.target.checked)} className="h-4 w-4 accent-[var(--primary)]" />
          <Label htmlFor="ass-adit" className="cursor-pointer">Assinado</Label>
        </div>
        <div className="space-y-1.5">
          <Label>Data início</Label>
          <Input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Data fim</Label>
          <Input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
        </div>
        <div className="space-y-1.5 col-span-2">
          <Label>Observação {tipo !== 'prazo' ? '(aproveitada na NF)' : ''}</Label>
          <textarea value={observacao} onChange={(e) => setObservacao(e.target.value)} rows={2}
            className="flex w-full rounded-md border border-border bg-input px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        </div>
      </div>

      {tipo !== 'prazo' && (
        <div className="mt-4">
          <Label className="mb-2 block">Serviços</Label>
          <ServicoEditor value={servicoRows} onChange={setServicoRows} servicos={servicos} />
        </div>
      )}

      {error && <p className="text-sm text-destructive mt-3">{error}</p>}
    </Dialog>
  )
}

function seedFromBase(base) {
  return (base || []).map((s) => ({
    servicoID: s.servicoID,
    quantidade: s.quantidade,
    valorUnitario: s.valorUnitario,
    fatura: s.fatura,
  }))
}
